/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo } from 'react'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Table } from '@nexigendigital/nxui'
import { shopAPI } from 'api/shop'
import { PendingItem } from 'models/shop'
import { getDomainName } from 'helpers/utils'
import { useAppSelector } from 'store/hooks'

import DomainWithExtension from 'containers/shop/domains/domainWithExtension'
import BillingCyclePricing from 'containers/shop/domains/billingCyclePricing'
import AutoRenewToggle from 'containers/shop/domains/autoRenewToggle'
import IdProtectionToggle from 'containers/shop/domains/idProtectionToggle'
import EligibilityRequirement from 'containers/shop/domains/eligibilityRequirement'
import SelectItem from 'containers/shop/domains/selectItem'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function SearchResult() {
    const {
        items,
        searchTerm,
        cart,
        productType
    } = useAppSelector((state) => state.shop)
    const { data, isFetching, isSuccess, isError } = shopAPI.endpoints.domains.useQueryState(searchTerm)
    const domain = getDomainName(searchTerm)
    const columns = useMemo(() => [
        {
            Header: 'Domain',
            accessor: 'domain'
        },
        {
            Header: 'Price',
            accessor: 'price'
        },
        {
            Header: 'Auto-Renew',
            accessor: 'autoRenew'
        },
        {
            Header: 'ID Protection',
            accessor: 'idProtection'
        },
        {
            Header: 'Eligibility Requirement',
            accessor: 'eligibilityRequirement'
        },
        {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ], [])
    const result = useMemo(() => {
        if (isSuccess && data) {
            return [data.availability, ...data.suggestion].map((item) => {
                const {
                    product: { id, name: tld },
                    pricing,
                    premium,
                    meta: { allow_id_protection },
                    eligibility_fields
                } = item
                const domainName = `${domain}${tld}`
                const addedItem = items.find((pendingItem: PendingItem) => pendingItem.name === domainName && pendingItem.product_id === id)

                return {
                    domain: (
                        <DomainWithExtension
                            domain={domain}
                            tld={tld}
                            premium={premium}
                        />
                    ),
                    price: (
                        <BillingCyclePricing
                            id={id}
                            domainName={domainName}
                            pricing={pricing}
                            items={items}
                            addedItem={addedItem}
                        />
                    ),
                    autoRenew: (
                        <AutoRenewToggle
                            id={id}
                            domainName={domainName}
                            premium={premium}
                            addedItem={addedItem}
                        />
                    ),
                    idProtection: (
                        <IdProtectionToggle
                            id={id}
                            domainName={domainName}
                            idProtection={allow_id_protection}
                            addedItem={addedItem}
                        />
                    ),
                    eligibilityRequirement: (
                        <EligibilityRequirement
                            eligibilityFields={eligibility_fields}
                        />
                    ),
                    action: (
                        <SelectItem
                            item={item}
                            items={items}
                            addedItem={addedItem}
                            productType={productType}
                            domainName={domainName}
                            cart={cart}
                        />
                    )
                }
            })
        }

        return []
    }, [isSuccess, data, items])

    /**********************************************************************************************************
     *   RENDER
     **********************************************************************************************************/
    if (isError) {
        return null
    }

    return (
        <StyledSearchResult>
            <Table
                render={{
                    columns: () => columns,
                    data: () => result
                }}
                conditions={{
                    sort: false,
                    pagination: false,
                    loading: isFetching
                }}
            />
        </StyledSearchResult>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const StyledSearchResult = styled.div`
    table {
        thead {
            tr {
                th:nth-child(2), th:nth-child(3), th:nth-child(4) {
                    span {
                        justify-content: center;
                    }
                }

                th:nth-child(5) {
                    max-width: 100px;
                    text-align: center;
                }
            }
        }

        tbody {
            tr {
                border-left: none;
                border-right: none;

                &:last-child {
                    border-bottom: none;
                }
            }
        }
    }
`
